/**
 * Agent-Level Sidebar Navigation
 *
 * Shown when a user is inside a specific chatbot/agent context.
 * Provides navigation to agent sub-pages grouped into logical sections.
 * Uses the same shadcn sidebar primitives as the org-level AppSidebar.
 */

import React, { useEffect, useState } from 'react'
import { NavLink, useParams, useLocation, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  LayoutDashboard,
  MessageSquare,
  BarChart3,
  BookOpen,
  Users,
  Lightbulb,
  Radio,
  Webhook,
  Settings,
  Bot,
  LogOut,
  CreditCard,
  ChevronsUpDown,
  Check,
} from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useAgentContext } from './AgentContextLayout'
import { chatbotsService } from '../../services/chatbots'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '../ui/sidebar'
import { Avatar, AvatarFallback } from '../ui/Avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/ShadcnDropdownMenu'

interface AgentListItem {
  id: string
  name: string
  is_active?: boolean
}

interface AgentNavItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
}

interface NavGroup {
  label?: string
  items: AgentNavItem[]
}

/** Build navigation groups for the current agent */
function buildNavGroups(chatbotId: string): NavGroup[] {
  const base = `/dashboard/chatbots/${chatbotId}`
  return [
    {
      items: [
        { name: 'Overview', href: base, icon: LayoutDashboard },
        { name: 'Conversations', href: `${base}/activity`, icon: MessageSquare },
        { name: 'Analytics', href: `${base}/analytics`, icon: BarChart3 },
      ],
    },
    {
      label: 'Manage',
      items: [
        { name: 'Knowledge', href: `${base}/data-sources`, icon: BookOpen },
        { name: 'Leads', href: `${base}/contacts`, icon: Users },
        { name: 'Suggestions', href: `${base}/suggestions`, icon: Lightbulb },
      ],
    },
    {
      label: 'Publish',
      items: [
        { name: 'Deploy', href: `${base}/deploy`, icon: Radio },
        { name: 'Webhooks', href: `${base}/webhooks`, icon: Webhook },
      ],
    },
  ]
}

/** Determine if a nav item is active based on current pathname */
function isItemActive(href: string, pathname: string, chatbotId: string): boolean {
  const base = `/dashboard/chatbots/${chatbotId}`
  // Overview is the index route -- only exact match
  if (href === base) return pathname === base
  return pathname.startsWith(href)
}

export function AgentSidebar() {
  const { chatbotId } = useParams<{ chatbotId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const { chatbot } = useAgentContext()
  const [agents, setAgents] = useState<AgentListItem[]>([])

  // Fetch all agents for the switcher
  useEffect(() => {
    chatbotsService.getChatbots(1, 100).then((res) => {
      const items = (res.items || []).map((c) => ({
        id: c.id,
        name: c.name,
        is_active: c.is_active,
      }))
      setAgents(items)
    }).catch(() => { /* ignore */ })
  }, [])

  const navGroups = buildNavGroups(chatbotId!)

  const handleLogout = async () => {
    try {
      await logout()
    } catch {
      // ignore
    } finally {
      window.location.href = '/auth/login'
    }
  }

  const userInitial =
    user?.full_name?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() ||
    'U'

  return (
    <Sidebar collapsible="icon">
      {/* Agent switcher */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
                  tooltip={chatbot.name}
                >
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                    <Bot className="size-4" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{chatbot.name}</span>
                    <span className={`truncate text-xs ${chatbot.is_active ? 'text-green-500' : 'text-muted-foreground'}`}>
                      {chatbot.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="start"
                sideOffset={4}
              >
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Agents
                </DropdownMenuLabel>
                {agents.map((agent) => (
                  <DropdownMenuItem
                    key={agent.id}
                    onClick={() => navigate(`/dashboard/chatbots/${agent.id}`)}
                    className="gap-2"
                  >
                    <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                      <Bot className="size-3.5" />
                    </div>
                    <span className="flex-1 truncate">{agent.name}</span>
                    {agent.id === chatbotId && (
                      <Check className="size-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/chatbots')} className="gap-2">
                  <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                    <Bot className="size-3.5" />
                  </div>
                  <span>All Agents</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard')} className="gap-2">
                  <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                    <ArrowLeft className="size-3.5" />
                  </div>
                  <span>Back to Home</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* Navigation groups */}
      <SidebarContent>
        {navGroups.map((group, idx) => (
          <SidebarGroup key={idx}>
            {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const active = isItemActive(item.href, location.pathname, chatbotId!)
                  return (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton asChild isActive={active} tooltip={item.name}>
                        <NavLink to={item.href}>
                          <item.icon />
                          <span>{item.name}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {/* User profile footer */}
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  tooltip={user?.full_name || 'User'}
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                      {userInitial}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user?.full_name || 'User'}
                    </span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg">
                        {userInitial}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {user?.full_name || 'User'}
                      </span>
                      <span className="truncate text-xs">{user?.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                  <Settings className="mr-2 size-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/plans')}>
                  <CreditCard className="mr-2 size-4" />
                  Plans & Billing
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 size-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

export default AgentSidebar
